import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAdminAllBanners, useAdminDeleteBanner } from 'hooks/useBanners';
import { IconEdit, IconTrash } from '@tabler/icons';
import MainCard from 'ui-component/cards/MainCard';

export default function BannerList() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {
        data: bannerData,
        isLoading,
        refetch
    } = useAdminAllBanners({
        page: page + 1,
        limit: rowsPerPage
    });

    const deleteBannerMutation = useAdminDeleteBanner();

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            try {
                await deleteBannerMutation.mutateAsync(id);
                refetch();
            } catch (error) {
                console.error('Failed to delete banner:', error);
            }
        }
    };

    const banners = bannerData?.data || [];
    const total = bannerData?.total || 0;
    const skeletonRows = Array.from({ length: rowsPerPage });

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'home_top':
                return 'Home Top';
            case 'home_middle':
                return 'Home Middle';
            case 'home_bottom':
                return 'Home Bottom';
            case 'category':
                return 'Category';
            default:
                return type;
        }
    };

    return (
        <MainCard
            title="Banner Management"
            secondary={
                <Button variant="contained" color="primary" onClick={() => navigate('/admin/banners/new')}>
                    Add Banner
                </Button>
            }
        >
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader aria-label="banner table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                skeletonRows.map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <Skeleton variant="rectangular" width={100} height={50} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={150} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={100} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={200} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={80} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Skeleton width={60} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : banners.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No banners found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                banners.map((banner) => (
                                    <TableRow hover key={banner.id}>
                                        <TableCell>
                                            <Avatar src={banner.image} variant="rounded" sx={{ width: 100, height: 50 }} />
                                        </TableCell>
                                        <TableCell>{banner.name}</TableCell>
                                        <TableCell>
                                            <Chip label={getTypeLabel(banner.type)} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{banner.link || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={banner.status === 'active' ? 'Active' : 'Inactive'}
                                                color={banner.status === 'active' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <IconButton color="primary" onClick={() => navigate(`/admin/banners/${banner.id}`)}>
                                                    <IconEdit size={18} />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDelete(banner.id)}>
                                                    <IconTrash size={18} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </MainCard>
    );
}
